/**
 * OrganizationDashboard - Dashboard view showing organization-wide metrics
 *
 * Displays:
 * - Quick insight cards with key metrics (PR throughput, features, bugfixes)
 * - Data table with detailed breakdown
 * - Stacked bar charts showing commit distribution by classification and size
 */

import {
  ActivityHeatmap,
  Avatar,
  AvatarFallback,
  AvatarImage,
  DataTable,
  DivergingBarChart,
  GenerativeAvatar,
  InsightDashboard,
  InsightDashboardStore,
  MetricsCard,
  StackedBarChart,
  type TabDefinition,
  TabsWithStore,
} from "@getdevpulse/shared-ui"
import { GitCommit, GitMerge, GitPullRequest, Tag } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useMemo } from "react"
import { updateUrlParams } from "../hooks/useUrlParams"
import { logger } from "../lib/logger"
import {
  calculateAverageDailyPerformance,
  calculateMetrics,
  calculatePerformanceScore,
} from "../lib/performanceScore"
import type { ClassificationType } from "../stores/SettingsStore"
import {
  useDataStore,
  useI18nStore,
  useMetadataStore,
  useSettingsStore,
  useViewStore,
} from "../stores/StoreContext"
import type { ComplexityClass } from "../types/data"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

// Helper to safely encode GitHub handles for URLs
const encodeGitHubHandle = (handle: string): string => {
  // Remove any characters that aren't valid in GitHub usernames
  // GitHub usernames can only contain alphanumeric characters and hyphens
  return encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))
}

// Helper to get GitHub avatar URL with validation
const getGitHubAvatarUrl = (handle: string, size = 64): string => {
  const safeHandle = encodeGitHubHandle(handle)
  return `https://github.com/${safeHandle}.png?size=${size}`
}

interface OrganizationDashboardProps {
  timeRange?: TimeRange
  /** Currently selected tab ID (controlled mode) */
  selectedTab?: string
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void
}

// Helper to get the time range translation key
const getTimeRangeKey = (
  timeRange: TimeRange,
): "timeLast7Days" | "timeLast14Days" | "timeLast28Days" | "timeLast8Weeks" | "timeLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "timeLast7Days"
    case "14days":
      return "timeLast14Days"
    case "28days":
      return "timeLast28Days"
    case "8weeks":
      return "timeLast8Weeks"
    case "12weeks":
      return "timeLast12Weeks"
    default:
      return "timeLast7Days"
  }
}

// Helper to get the comparison translation key
const getComparisonKey = (
  timeRange: TimeRange,
):
  | "quickInsightVsLast7Days"
  | "quickInsightVsLast14Days"
  | "quickInsightVsLast28Days"
  | "quickInsightVsLast8Weeks"
  | "quickInsightVsLast12Weeks" => {
  switch (timeRange) {
    case "7days":
      return "quickInsightVsLast7Days"
    case "14days":
      return "quickInsightVsLast14Days"
    case "28days":
      return "quickInsightVsLast28Days"
    case "8weeks":
      return "quickInsightVsLast8Weeks"
    case "12weeks":
      return "quickInsightVsLast12Weeks"
    default:
      return "quickInsightVsLast7Days"
  }
}

// Helper to get date range start based on time range
const getDateRangeStart = (timeRange: TimeRange): Date => {
  // Use yesterday as the reference point to exclude the current incomplete day
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // 14 days including yesterday
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // 28 days including yesterday
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (8 * 7 - 1) * 24 * 60 * 60 * 1000) // 8 weeks including yesterday
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (12 * 7 - 1) * 24 * 60 * 60 * 1000) // 12 weeks including yesterday
  }
  return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to get comparison period start based on time range
const getComparisonPeriodStart = (timeRange: TimeRange): Date => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(23, 59, 59, 999)

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // Previous 7 days
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // Previous 14 days
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 55 * 24 * 60 * 60 * 1000) // Previous 28 days
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (16 * 7 - 1) * 24 * 60 * 60 * 1000) // Previous 8 weeks
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (24 * 7 - 1) * 24 * 60 * 60 * 1000) // Previous 12 weeks
  }
  return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // Default to 14 days ago
}

// Helper to get number of weeks to show based on time range
const getWeeksToShow = (timeRange: TimeRange): number => {
  if (timeRange === "7days") {
    return 1 // Show at least 1 week for 7 days
  }
  if (timeRange === "14days") {
    return 2
  }
  if (timeRange === "28days") {
    return 4
  }
  if (timeRange === "8weeks") {
    return 8
  }
  if (timeRange === "12weeks") {
    return 12
  }
  return 6 // Default to 6 weeks
}

export const OrganizationDashboard = observer(
  ({ timeRange = "7days", selectedTab, onTabChange }: OrganizationDashboardProps) => {
    const dataStore = useDataStore()
    const metadataStore = useMetadataStore()
    const i18nStore = useI18nStore()
    const settingsStore = useSettingsStore()
    const viewStore = useViewStore()

    // Get the time range label for insight cards
    const timeRangeLabel = i18nStore.t(getTimeRangeKey(timeRange))
    // Get the comparison translation key
    const comparisonKey = getComparisonKey(timeRange)

    // Navigate to contributor page
    const navigateToContributor = useCallback(
      (handle: string) => {
        // Update URL params to show individual view with selected contributor
        updateUrlParams({ view: "individual", contributor: handle })
        // Trigger ViewStore update
        viewStore.setView("individual")
      },
      [viewStore],
    )

    // Navigate to team page
    const navigateToTeam = useCallback(
      (teamName: string) => {
        // Update URL params to show group view with selected team
        updateUrlParams({ view: "group", team: teamName })
        // Trigger ViewStore update
        viewStore.setView("group")
      },
      [viewStore],
    )

    // Calculate date ranges based on selected time range
    const dateRange = useMemo(() => getDateRangeStart(timeRange), [timeRange])
    const comparisonDateRange = useMemo(() => getComparisonPeriodStart(timeRange), [timeRange])

    // Create the dashboard store with computed data
    const dashboardStore = useMemo(() => {
      const commits = dataStore.allCommits
      const prs = dataStore.allPullRequests

      // Use computed date ranges (up to yesterday)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(23, 59, 59, 999)
      const currentPeriodStart = dateRange
      const previousPeriodStart = comparisonDateRange

      // Helper function to generate daily chart data
      const generateDailyChartData = (
        items: Array<{ timestampDate?: Date; createdAtDate?: Date }>,
        startDate: Date,
        endDate: Date,
      ) => {
        const dailyCounts = new Map<string, number>()
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)

        // Initialize all days with 0
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split("T")[0]
          dailyCounts.set(dateKey, 0)
        }

        // Count items per day
        items.forEach((item) => {
          const date = item.timestampDate || item.createdAtDate
          if (date && date >= start && date <= end) {
            const dateKey = date.toISOString().split("T")[0]
            dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
          }
        })

        // Filter out weekends with no activity
        return Array.from(dailyCounts.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .filter(([dateKey, value]) => {
            const date = new Date(dateKey)
            const dayOfWeek = date.getDay()
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
            // Include if it's not a weekend, or if it's a weekend with activity
            return !isWeekend || value > 0
          })
          .map(([, value]) => ({ value }))
      }

      // Current period metrics (based on selected time range)
      const currentWeekPRs = prs.filter(
        (pr) => pr.createdAtDate >= currentPeriodStart && pr.state === "merged",
      )
      const currentWeekFeatures = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "feature",
      )
      const currentWeekBugfixes = commits.filter(
        (c) => c.timestampDate >= currentPeriodStart && c.commitType === "bugfix",
      )
      const currentWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      // Previous period metrics (for comparison)
      const previousWeekPRs = prs.filter(
        (pr) =>
          pr.createdAtDate >= previousPeriodStart &&
          pr.createdAtDate < currentPeriodStart &&
          pr.state === "merged",
      )
      const previousWeekFeatures = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "feature",
      )
      const previousWeekBugfixes = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType === "bugfix",
      )
      const previousWeekMaintenance = commits.filter(
        (c) =>
          c.timestampDate >= previousPeriodStart &&
          c.timestampDate < currentPeriodStart &&
          c.commitType &&
          c.commitType !== "feature" &&
          c.commitType !== "bugfix",
      )

      logger.debug("Calculated organization metrics", {
        component: "OrganizationDashboard",
        currentWeekPRs: currentWeekPRs.length,
        currentWeekFeatures: currentWeekFeatures.length,
        currentWeekBugfixes: currentWeekBugfixes.length,
        currentWeekMaintenance: currentWeekMaintenance.length,
      })

      // Calculate organization-wide performance score
      const allCurrentCommits = commits.filter((c) => c.timestampDate >= currentPeriodStart)
      const allCurrentIssues = dataStore.allIssues.filter(
        (issue) => issue.closedAtDate && issue.closedAtDate >= currentPeriodStart,
      )

      // Calculate average daily performance score (excluding weekends with no activity)
      const avgDailyScore = calculateAverageDailyPerformance(
        allCurrentCommits,
        currentWeekPRs,
        allCurrentIssues,
        currentPeriodStart,
        yesterday,
      )

      // Create a performance score object with the calculated average
      const performanceScore = {
        overall: avgDailyScore,
        breakdown: {
          quality: 0,
          throughput: 0,
          impact: 0,
          problemSolving: 0,
          versatility: 0,
        },
        metrics: {
          commitScore: 0,
          commitCount: 0,
          linesAdded: 0,
          linesRemoved: 0,
          filesChanged: 0,
          prCount: 0,
          prAdditions: 0,
          prDeletions: 0,
          issuesResolved: 0,
          featureCommits: 0,
          bugfixCommits: 0,
          refactoringCommits: 0,
          otherCommits: 0,
        },
      }

      // Generate daily performance values for the chart
      const dailyPerformanceData = generateDailyChartData(
        allCurrentCommits,
        currentPeriodStart,
        yesterday,
      ).map((_, index) => {
        const dayStart = new Date(currentPeriodStart)
        dayStart.setDate(dayStart.getDate() + index)
        dayStart.setHours(0, 0, 0, 0)
        const dayEnd = new Date(dayStart)
        dayEnd.setHours(23, 59, 59, 999)

        const dayCommits = allCurrentCommits.filter(
          (c) => c.timestampDate >= dayStart && c.timestampDate <= dayEnd,
        )
        const dayPRs = currentWeekPRs.filter(
          (pr) => pr.createdAtDate && pr.createdAtDate >= dayStart && pr.createdAtDate <= dayEnd,
        )
        const dayIssues = allCurrentIssues.filter(
          (issue) =>
            issue.closedAtDate && issue.closedAtDate >= dayStart && issue.closedAtDate <= dayEnd,
        )

        const dayMetrics = calculateMetrics(dayCommits, dayPRs, dayIssues)
        const dayScore = calculatePerformanceScore(dayMetrics, 1)

        return {
          value: dayScore.overall,
          date: dayStart,
          metrics: dayMetrics,
          breakdown: dayScore.breakdown,
        }
      })

      const dailyPerformanceValues = dailyPerformanceData.map((d) => ({ value: d.value }))

      // Calculate trend for performance score (compare first half vs second half)
      const halfwayPoint = Math.floor(dailyPerformanceData.length / 2)
      const firstHalfAvg =
        dailyPerformanceData.slice(0, halfwayPoint).reduce((sum, d) => sum + d.value, 0) /
          halfwayPoint || 0
      const secondHalfAvg =
        dailyPerformanceData.slice(halfwayPoint).reduce((sum, d) => sum + d.value, 0) /
          (dailyPerformanceData.length - halfwayPoint) || 0
      const performanceTrend = secondHalfAvg - firstHalfAvg

      // Create insights with their associated table data
      const insights = [
        {
          id: "pr-throughput",
          insight: {
            id: "pr-throughput",
            subtitle: i18nStore.t("insightMergedPRs"),
            date: timeRangeLabel,
            title: i18nStore.t("insightPRThroughput"),
            value: currentWeekPRs.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekPRs.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekPRs, currentPeriodStart, yesterday),
          },
          tableData: currentWeekPRs.map((pr) => ({
            title: pr.title,
            repoName: pr.repoName,
            url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
            author: pr.creatorHandle,
            date: pr.createdAtDate.toLocaleDateString(),
            additions: pr.additions?.toString() || "0",
            deletions: pr.deletions?.toString() || "0",
            files: pr.changedFiles?.toString() || "0",
          })),
          tableColumns: [
            {
              accessorKey: "title",
              header: i18nStore.t("tableColumnTitle"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string; repoName: string }
                  getValue: (key: string) => string
                }
              }) => (
                <div className="flex flex-col gap-0.5 max-w-md">
                  <a
                    href={row.original.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline wrap-break-word"
                  >
                    {row.getValue("title")}
                  </a>
                  <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
                </div>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = getGitHubAvatarUrl(handle)
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            {
              accessorKey: "date",
              header: i18nStore.t("tableColumnCreated"),
              sortDescFirst: true,
            },
            {
              accessorKey: "additions",
              header: i18nStore.t("tableColumnAdditions"),
            },
            {
              accessorKey: "deletions",
              header: i18nStore.t("tableColumnDeletions"),
            },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "features",
          insight: {
            id: "features",
            subtitle: i18nStore.t("insightFeatureCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightFeatures"),
            value: currentWeekFeatures.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekFeatures.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekFeatures, currentPeriodStart, yesterday),
          },
          tableData: currentWeekFeatures.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            repoName: commit.repoName,
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string; repoName: string }
                  getValue: (key: string) => string
                }
              }) => (
                <div className="flex flex-col gap-0.5">
                  <a
                    href={row.original.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {row.getValue("message")}
                  </a>
                  <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
                </div>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = getGitHubAvatarUrl(handle)
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "bugfixes",
          insight: {
            id: "bugfixes",
            subtitle: i18nStore.t("insightBugfixCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightBugfixes"),
            value: currentWeekBugfixes.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekBugfixes.length.toString(),
            }),
            chartData: generateDailyChartData(currentWeekBugfixes, currentPeriodStart, yesterday),
          },
          tableData: currentWeekBugfixes.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            repoName: commit.repoName,
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string; repoName: string }
                  getValue: (key: string) => string
                }
              }) => (
                <div className="flex flex-col gap-0.5">
                  <a
                    href={row.original.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {row.getValue("message")}
                  </a>
                  <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
                </div>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = getGitHubAvatarUrl(handle)
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
        {
          id: "performance",
          insight: {
            id: "performance",
            subtitle: i18nStore.t("performanceScoreTitle"),
            date: timeRangeLabel,
            title: i18nStore.t("tabPerformance"),
            value: performanceScore.overall.toFixed(1),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: (performanceTrend >= 0 ? "+" : "") + performanceTrend.toFixed(1),
            }),
            chartData: dailyPerformanceValues,
          },
          tableData: dailyPerformanceData
            .map((dayData) => {
              const m = dayData.metrics
              const b = dayData.breakdown

              // Build concise reasoning based on actual activity and its impact
              const reasons = []
              if (m.commitCount > 0) {
                reasons.push(`${m.commitCount} commits`)
              }
              if (m.prCount > 0) {
                reasons.push(`${m.prCount} PRs`)
              }
              if (m.linesAdded + m.linesRemoved > 0) {
                reasons.push(`${m.linesAdded + m.linesRemoved} lines`)
              }
              if (m.issuesResolved > 0) {
                reasons.push(`${m.issuesResolved} issues`)
              }

              // Add component scores to show impact on overall score
              const impactScores = []
              if (b.quality > 0) impactScores.push(`Q:${b.quality.toFixed(0)}`)
              if (b.throughput > 0) impactScores.push(`T:${b.throughput.toFixed(0)}`)
              if (b.impact > 0) impactScores.push(`I:${b.impact.toFixed(0)}`)
              if (b.problemSolving > 0) impactScores.push(`PS:${b.problemSolving.toFixed(0)}`)
              if (b.versatility > 0) impactScores.push(`V:${b.versatility.toFixed(0)}`)

              const activityText = reasons.length > 0 ? reasons.join(", ") : "No activity"
              const impactText = impactScores.length > 0 ? ` → ${impactScores.join(", ")}` : ""

              // Add formula explanation showing how the score is calculated
              // Format: Q×30% + T×25% + I×20% + PS×15% + V×10%
              const formulaParts = []
              if (b.quality > 0) formulaParts.push(`${b.quality.toFixed(0)}×30%`)
              if (b.throughput > 0) formulaParts.push(`${b.throughput.toFixed(0)}×25%`)
              if (b.impact > 0) formulaParts.push(`${b.impact.toFixed(0)}×20%`)
              if (b.problemSolving > 0) formulaParts.push(`${b.problemSolving.toFixed(0)}×15%`)
              if (b.versatility > 0) formulaParts.push(`${b.versatility.toFixed(0)}×10%`)

              const formulaText = formulaParts.length > 0 ? ` = ${formulaParts.join(" + ")}` : ""

              // Get weekday name
              const weekdayNames = [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ]
              const weekday = weekdayNames[dayData.date.getDay()]

              // Check if it's a weekend with no activity
              const isWeekend = dayData.date.getDay() === 0 || dayData.date.getDay() === 6
              const hasActivity = m.commitCount > 0 || m.prCount > 0 || m.issuesResolved > 0
              const isExcluded = isWeekend && !hasActivity

              return {
                date: dayData.date.toLocaleDateString(),
                weekday: weekday,
                value: dayData.value.toFixed(1),
                reasoning: activityText + impactText + formulaText,
                isExcluded: isExcluded,
              }
            })
            .reverse(), // Most recent first
          tableColumns: [
            {
              accessorKey: "date",
              header: i18nStore.t("tableColumnDate"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; date: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.date}
                </span>
              ),
            },
            {
              accessorKey: "weekday",
              header: i18nStore.t("tableColumnWeekday"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; weekday: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.weekday}
                </span>
              ),
            },
            {
              accessorKey: "value",
              header: i18nStore.t("tableColumnValue"),
              cell: ({ row }: { row: { original: { isExcluded: boolean; value: string } } }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.value}
                </span>
              ),
            },
            {
              accessorKey: "reasoning",
              header: i18nStore.t("tableColumnReasoning"),
              cell: ({
                row,
              }: {
                row: { original: { isExcluded: boolean; reasoning: string } }
              }) => (
                <span className={row.original.isExcluded ? "text-muted-foreground" : ""}>
                  {row.original.reasoning}
                </span>
              ),
            },
          ],
        },
        // Maintenance insight kept for potential future use
        {
          id: "maintenance",
          insight: {
            id: "maintenance",
            subtitle: i18nStore.t("insightMaintenanceCommits"),
            date: timeRangeLabel,
            title: i18nStore.t("insightMaintenance"),
            value: currentWeekMaintenance.length.toString(),
            secondaryText: i18nStore.tf(comparisonKey, {
              count: previousWeekMaintenance.length.toString(),
            }),
            chartData: generateDailyChartData(
              currentWeekMaintenance,
              currentPeriodStart,
              yesterday,
            ),
          },
          tableData: currentWeekMaintenance.map((commit) => ({
            message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
            repoName: commit.repoName,
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
            author: commit.authorHandle,
            type: commit.commitType || "other",
            date: commit.timestampDate.toLocaleDateString(),
            score: commit.score.toString(),
            files: commit.filesChanged.toString(),
          })),
          tableColumns: [
            {
              accessorKey: "message",
              header: i18nStore.t("tableColumnMessage"),
              cell: ({
                row,
              }: {
                row: {
                  original: { url: string; repoName: string }
                  getValue: (key: string) => string
                }
              }) => (
                <div className="flex flex-col gap-0.5">
                  <a
                    href={row.original.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {row.getValue("message")}
                  </a>
                  <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
                </div>
              ),
            },
            {
              accessorKey: "author",
              header: i18nStore.t("tableColumnAuthor"),
              cell: ({
                row,
              }: {
                row: {
                  getValue: (key: string) => string
                }
              }) => {
                const handle = row.getValue("author") as string
                const avatarUrl = getGitHubAvatarUrl(handle)
                const fallback = handle.slice(0, 2).toUpperCase()
                return (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={avatarUrl} alt={handle} />
                      <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                    </Avatar>
                    <button
                      type="button"
                      onClick={() => navigateToContributor(handle)}
                      className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {handle}
                    </button>
                  </div>
                )
              },
            },
            { accessorKey: "type", header: i18nStore.t("tableColumnType") },
            { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
            { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
            { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
          ],
        },
      ]

      return InsightDashboardStore.create({
        insights: insights.slice(0, 4), // Only show pr-throughput, features, bugfixes, and performance
        selectedInsightId: "pr-throughput",
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale needed for translation reactivity
    }, [
      dataStore.allCommits,
      dataStore.allPullRequests,
      dataStore.allIssues,
      i18nStore.t,
      i18nStore.tf,
      dateRange,
      comparisonDateRange,
      timeRangeLabel,
      comparisonKey,
      navigateToContributor,
    ])

    // Compute bar chart data for commits (all commits, not filtered by week)
    const barChartData = useMemo(() => {
      const commits = dataStore.allCommits
      const selectedClassifications = settingsStore.selectedClassificationsArray

      // Define the order of complexity classes and commit types
      const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
      const commitTypeOrder = [
        "feature",
        "bugfix",
        "refactoring",
        "ci",
        "tests",
        "docs",
        "chore",
        "perf",
        "revert",
        "merge",
        "archive",
        "styling", // Renamed from "style" to avoid React reserved prop conflict
        "other",
      ]

      // Map commit types to avoid reserved words
      // "style" -> "styling" to prevent React style prop conflicts
      const mapCommitType = (type: string | undefined): string => {
        if (type === "style") return "styling"
        return type || "unclassified"
      }

      // Filter commits by selected classifications
      // Note: Use original commitType for filtering, mapCommitType is only for display
      const filteredCommits = commits.filter((commit) => {
        const classType = (commit.commitType || "unclassified") as ClassificationType
        return selectedClassifications.includes(classType)
      })

      // Chart 1: Classification (x-axis) stacked by Size
      // Group commits by commitType, then count by complexityClass
      const byClassification = new Map<string, Record<ComplexityClass, number>>()

      for (const commitType of commitTypeOrder) {
        byClassification.set(commitType, {
          XS: 0,
          S: 0,
          M: 0,
          L: 0,
          XL: 0,
        })
      }

      // Also handle commits without a type (undefined)
      byClassification.set("unclassified", {
        XS: 0,
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
      })

      for (const commit of filteredCommits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = byClassification.get(type)
        if (counts && size in counts) {
          counts[size] = (counts[size] || 0) + 1
        }
      }

      const classificationData = Array.from(byClassification.entries())
        .filter(([type, counts]) => {
          // Only include types that have commits
          // Note: selectedClassifications check is redundant since filteredCommits is already filtered
          // But we need to map "styling" back to "style" for the check
          const originalType = (type === "styling" ? "style" : type) as ClassificationType
          return (
            selectedClassifications.includes(originalType) &&
            Object.values(counts).some((v) => v > 0)
          )
        })
        .map(([type, counts]) => ({
          classification: type,
          ...counts,
        }))

      // Chart 2: Size (x-axis) stacked by Classification
      // Group commits by complexityClass, then count by commitType
      const bySize = new Map<ComplexityClass, Record<string, number>>()

      for (const size of complexityOrder) {
        const typeCounts: Record<string, number> = {}
        for (const type of [...commitTypeOrder, "unclassified"]) {
          typeCounts[type] = 0
        }
        bySize.set(size, typeCounts)
      }

      for (const commit of filteredCommits) {
        const type = mapCommitType(commit.commitType)
        const size = commit.complexityClass
        const counts = bySize.get(size)
        if (counts) {
          counts[type] = (counts[type] || 0) + 1
        }
      }

      // Filter to only include types that have at least one commit across all sizes and are selected
      const activeTypes = new Set<string>()
      for (const [, counts] of bySize.entries()) {
        for (const [type, count] of Object.entries(counts)) {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = (type === "styling" ? "style" : type) as ClassificationType
          if (count > 0 && selectedClassifications.includes(originalType)) {
            activeTypes.add(type)
          }
        }
      }

      const sizeData = complexityOrder.map((size) => {
        const counts = bySize.get(size)
        if (!counts) return { size }
        const entry: Record<string, string | number> = { size }
        for (const type of activeTypes) {
          entry[type] = counts[type] || 0
        }
        return entry
      })

      return {
        classificationData,
        sizeData,
        complexityOrder,
        activeTypes: Array.from(activeTypes),
      }
    }, [dataStore.allCommits, settingsStore.selectedClassificationsArray])

    // Calculate weekly commit scores data for the Score tab
    const weeklyScoresData = useMemo(() => {
      const commits = dataStore.allCommits
      const selectedClassifications = settingsStore.selectedClassificationsArray
      const now = new Date()
      const sixWeeksAgo = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000)

      // Map commit types to avoid reserved words
      const mapCommitType = (type: string | undefined): string => {
        if (type === "style") return "styling"
        return type || "unclassified"
      }

      // Filter commits from last 6 weeks and by selected classifications
      // Note: Use original commitType for filtering, mapCommitType is only for display
      const recentCommits = commits.filter((c) => {
        const classType = (c.commitType || "unclassified") as ClassificationType
        return c.timestampDate >= sixWeeksAgo && selectedClassifications.includes(classType)
      })

      if (recentCommits.length === 0) {
        return {
          scoresBySize: [],
          scoresByClassification: [],
          complexityOrder: [],
          classifications: [],
        }
      }

      // Get the earliest commit date to determine actual weeks available
      const earliestDate = new Date(
        Math.min(...recentCommits.map((c) => c.timestampDate.getTime())),
      )
      const weeksAvailable = Math.min(
        6,
        Math.ceil((now.getTime() - earliestDate.getTime()) / (7 * 24 * 60 * 60 * 1000)),
      )

      // Helper function to get week label
      const getWeekLabel = (weekIndex: number, totalWeeks: number): string => {
        if (weekIndex === totalWeeks - 1) return "Current Week"
        if (weekIndex === totalWeeks - 2) return "Last Week"

        // Get the first day of the week (Monday)
        const weekStart = new Date(
          now.getTime() - (totalWeeks - weekIndex) * 7 * 24 * 60 * 60 * 1000,
        )
        const monday = new Date(weekStart)
        monday.setDate(weekStart.getDate() - weekStart.getDay() + 1)

        const month = String(monday.getMonth() + 1).padStart(2, "0")
        const day = String(monday.getDate()).padStart(2, "0")
        return `${month}/${day}`
      }

      // Group commits by week
      const weeklyData = new Map<
        string,
        { bySize: Map<string, number>; byClass: Map<string, number> }
      >()

      for (let i = 0; i < weeksAvailable; i++) {
        const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000)
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
        const weekLabel = getWeekLabel(weeksAvailable - i - 1, weeksAvailable)

        const weekCommits = recentCommits.filter(
          (c) => c.timestampDate >= weekStart && c.timestampDate < weekEnd,
        )

        const bySize = new Map<string, number>()
        const byClass = new Map<string, number>()

        for (const commit of weekCommits) {
          const size = commit.complexityClass || "XS"
          const classification = mapCommitType(commit.commitType)

          bySize.set(size, (bySize.get(size) || 0) + commit.score)
          byClass.set(classification, (byClass.get(classification) || 0) + commit.score)
        }

        weeklyData.set(weekLabel, { bySize, byClass })
      }

      // Define the order
      const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]

      // Get only the classifications that are selected and have data
      const classificationsWithData = new Set<string>()
      for (const [, data] of weeklyData.entries()) {
        for (const [classification] of data.byClass.entries()) {
          // Map "styling" back to "style" for the selectedClassifications check
          const originalType = (
            classification === "styling" ? "style" : classification
          ) as ClassificationType
          if (selectedClassifications.includes(originalType)) {
            classificationsWithData.add(classification)
          }
        }
      }
      const classifications = Array.from(classificationsWithData)

      // Build data for stacked bar chart by size
      const scoresBySize = Array.from(weeklyData.entries())
        .reverse()
        .map(([week, data]) => {
          const entry: Record<string, string | number> = { week }
          for (const size of complexityOrder) {
            entry[size] = data.bySize.get(size) || 0
          }
          return entry
        })

      // Build data for stacked bar chart by classification
      const scoresByClassification = Array.from(weeklyData.entries())
        .reverse()
        .map(([week, data]) => {
          const entry: Record<string, string | number> = { week }
          for (const classification of classifications) {
            entry[classification] = data.byClass.get(classification) || 0
          }
          return entry
        })

      return {
        scoresBySize,
        scoresByClassification,
        complexityOrder,
        classifications,
      }
    }, [dataStore.allCommits, settingsStore.selectedClassificationsArray])

    // Calculate PR metrics for the new PR tab
    const prMetrics = useMemo(() => {
      const prs = dataStore.allPullRequests
      const now = new Date()
      const currentPeriodStart = dateRange
      const previousPeriodStart = comparisonDateRange

      // Current period PRs (based on selected time range)
      const currentPeriodPRs = prs.filter(
        (pr) => pr.mergedAtDate && pr.mergedAtDate >= currentPeriodStart && pr.state === "merged",
      )

      // Previous period PRs (for comparison)
      const previousPeriodPRs = prs.filter(
        (pr) =>
          pr.mergedAtDate &&
          pr.mergedAtDate >= previousPeriodStart &&
          pr.mergedAtDate < currentPeriodStart &&
          pr.state === "merged",
      )

      // Calculate percentage change
      const currentCount = currentPeriodPRs.length
      const previousCount = previousPeriodPRs.length
      const percentageChange =
        previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : 0

      // Generate daily PR count for chart (current period)
      const dailyPRs = new Map<string, number>()
      const start = new Date(currentPeriodStart)
      start.setHours(0, 0, 0, 0)
      const end = new Date(now)
      end.setHours(23, 59, 59, 999)

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyPRs.set(dateKey, 0)
      }

      currentPeriodPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          dailyPRs.set(dateKey, (dailyPRs.get(dateKey) || 0) + 1)
        }
      })

      const prChartData = Array.from(dailyPRs.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Calculate code changes (additions + deletions) for current period
      const dailyCodeChanges = new Map<string, { additions: number; deletions: number }>()
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyCodeChanges.set(dateKey, { additions: 0, deletions: 0 })
      }

      currentPeriodPRs.forEach((pr) => {
        if (pr.mergedAtDate) {
          const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
          const current = dailyCodeChanges.get(dateKey) || {
            additions: 0,
            deletions: 0,
          }
          dailyCodeChanges.set(dateKey, {
            additions: current.additions + pr.additions,
            deletions: current.deletions + pr.deletions,
          })
        }
      })

      const codeChangesChartData = Array.from(dailyCodeChanges.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { additions, deletions }]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: additions + deletions,
        }))

      // Calculate trend for code changes by comparing previous period
      const previousPeriodCodeChanges = previousPeriodPRs.reduce(
        (sum, pr) => sum + pr.additions + pr.deletions,
        0,
      )
      const currentPeriodCodeChanges = currentPeriodPRs.reduce(
        (sum, pr) => sum + pr.additions + pr.deletions,
        0,
      )
      const codeChangesTrend =
        previousPeriodCodeChanges > 0
          ? ((currentPeriodCodeChanges - previousPeriodCodeChanges) / previousPeriodCodeChanges) *
            100
          : 0

      return {
        currentCount,
        previousCount,
        percentageChange,
        prChartData,
        codeChangesChartData,
        codeChangesTrend,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
        codeChangesTrendDirection: (codeChangesTrend > 0
          ? "up"
          : codeChangesTrend < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
      }
    }, [dataStore.allPullRequests, dateRange, comparisonDateRange])

    // Calculate weekly PR data for the PRs tab
    const weeklyPRData = useMemo(() => {
      const prs = dataStore.allPullRequests.filter((pr) => pr.state === "merged")
      const now = new Date()
      const weeksToShow = getWeeksToShow(timeRange)
      const weeksAgo = new Date(now.getTime() - weeksToShow * 7 * 24 * 60 * 60 * 1000)

      // Filter PRs from the specified time range
      const recentPRs = prs.filter((pr) => pr.mergedAtDate && pr.mergedAtDate >= weeksAgo)

      if (recentPRs.length === 0) {
        return { prsByWeek: [], prsByWeekByTeam: [], codeChangesByWeek: [], teams: [] }
      }

      // Get the earliest PR date to determine actual weeks available
      const earliestDate = new Date(
        Math.min(
          ...recentPRs
            .filter((pr) => pr.mergedAtDate)
            .map((pr) => (pr.mergedAtDate as Date).getTime()),
        ),
      )
      const weeksAvailable = Math.min(
        weeksToShow,
        Math.ceil((now.getTime() - earliestDate.getTime()) / (7 * 24 * 60 * 60 * 1000)),
      )

      // Helper function to get week label
      const getWeekLabel = (weekIndex: number, totalWeeks: number): string => {
        if (weekIndex === totalWeeks - 1) return "Current Week"
        if (weekIndex === totalWeeks - 2) return "Last Week"

        // Get the first day of the week (Monday)
        const weekStart = new Date(
          now.getTime() - (totalWeeks - weekIndex) * 7 * 24 * 60 * 60 * 1000,
        )
        const monday = new Date(weekStart)
        monday.setDate(weekStart.getDate() - weekStart.getDay() + 1)

        const month = String(monday.getMonth() + 1).padStart(2, "0")
        const day = String(monday.getDate()).padStart(2, "0")
        return `${month}/${day}`
      }

      // Get all unique teams
      const allTeams = metadataStore.uniqueTeams
      const teamSet = new Set(allTeams)

      // Group PRs by week
      const weeklyData: {
        label: string
        count: number
        additions: number
        deletions: number
        byTeam: Map<string, number>
        codeChangesByTeam: Map<string, { additions: number; deletions: number }>
      }[] = []

      for (let i = 0; i < weeksAvailable; i++) {
        const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000)
        const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
        const weekLabel = getWeekLabel(weeksAvailable - i - 1, weeksAvailable)

        const weekPRs = recentPRs.filter(
          (pr) => pr.mergedAtDate && pr.mergedAtDate >= weekStart && pr.mergedAtDate < weekEnd,
        )

        const additions = weekPRs.reduce((sum, pr) => sum + pr.additions, 0)
        const deletions = weekPRs.reduce((sum, pr) => sum + pr.deletions, 0)

        // Count PRs by team
        const byTeam = new Map<string, number>()
        const codeChangesByTeam = new Map<string, { additions: number; deletions: number }>()

        // Initialize all teams with 0
        for (const team of allTeams) {
          byTeam.set(team, 0)
          codeChangesByTeam.set(team, { additions: 0, deletions: 0 })
        }

        // Count PRs and code changes per team
        weekPRs.forEach((pr) => {
          const team = metadataStore.getTeam(pr.creatorHandle)
          if (team && teamSet.has(team)) {
            byTeam.set(team, (byTeam.get(team) || 0) + 1)
            const current = codeChangesByTeam.get(team) || { additions: 0, deletions: 0 }
            codeChangesByTeam.set(team, {
              additions: current.additions + pr.additions,
              deletions: current.deletions + pr.deletions,
            })
          }
        })

        weeklyData.push({
          label: weekLabel,
          count: weekPRs.length,
          additions,
          deletions,
          byTeam,
          codeChangesByTeam,
        })
      }

      // Reverse to show oldest first
      weeklyData.reverse()

      // Format for regular bar chart (PR count)
      const prsByWeek = weeklyData.map((week) => ({
        label: week.label,
        value: week.count,
      }))

      // Format for stacked bar chart (PR count by team)
      const prsByWeekByTeam = weeklyData.map((week) => {
        const entry: Record<string, string | number> = { week: week.label }
        for (const team of allTeams) {
          entry[team] = week.byTeam.get(team) || 0
        }
        return entry
      })

      // Format for diverging bar chart (additions/deletions)
      const codeChangesByWeek = weeklyData.map((week) => ({
        label: week.label,
        positive: week.additions,
        negative: -week.deletions, // Negative for downward bars
      }))

      return { prsByWeek, prsByWeekByTeam, codeChangesByWeek, teams: allTeams }
    }, [dataStore.allPullRequests, metadataStore, timeRange])

    // Calculate Issue metrics for the new Issues tab
    const issueMetrics = useMemo(() => {
      const issues = dataStore.allIssues
      const now = new Date()
      const currentPeriodStart = dateRange
      const previousPeriodStart = comparisonDateRange

      // Current period issues created
      const currentPeriodCreated = issues.filter(
        (issue) => issue.createdAtDate >= currentPeriodStart,
      )

      // Previous period issues created (for comparison)
      const previousPeriodCreated = issues.filter(
        (issue) =>
          issue.createdAtDate >= previousPeriodStart && issue.createdAtDate < currentPeriodStart,
      )

      // Current period issues closed
      const currentPeriodClosed = issues.filter(
        (issue) =>
          issue.closedAtDate &&
          issue.closedAtDate >= currentPeriodStart &&
          issue.state === "closed",
      )

      // Previous period issues closed (for comparison)
      const previousPeriodClosed = issues.filter(
        (issue) =>
          issue.closedAtDate &&
          issue.closedAtDate >= previousPeriodStart &&
          issue.closedAtDate < currentPeriodStart &&
          issue.state === "closed",
      )

      // Calculate percentage changes
      const createdCount = currentPeriodCreated.length
      const previousCreatedCount = previousPeriodCreated.length
      const createdPercentageChange =
        previousCreatedCount > 0
          ? ((createdCount - previousCreatedCount) / previousCreatedCount) * 100
          : 0

      const closedCount = currentPeriodClosed.length
      const previousClosedCount = previousPeriodClosed.length
      const closedPercentageChange =
        previousClosedCount > 0
          ? ((closedCount - previousClosedCount) / previousClosedCount) * 100
          : 0

      // Generate daily issue created count for chart (current period)
      const dailyCreated = new Map<string, number>()
      const start = new Date(currentPeriodStart)
      start.setHours(0, 0, 0, 0)
      const end = new Date(now)
      end.setHours(23, 59, 59, 999)

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyCreated.set(dateKey, 0)
      }

      currentPeriodCreated.forEach((issue) => {
        const dateKey = issue.createdAtDate.toISOString().split("T")[0]
        dailyCreated.set(dateKey, (dailyCreated.get(dateKey) || 0) + 1)
      })

      const createdChartData = Array.from(dailyCreated.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      // Generate daily issue closed count for chart (current period)
      const dailyClosed = new Map<string, number>()
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyClosed.set(dateKey, 0)
      }

      currentPeriodClosed.forEach((issue) => {
        if (issue.closedAtDate) {
          const dateKey = issue.closedAtDate.toISOString().split("T")[0]
          dailyClosed.set(dateKey, (dailyClosed.get(dateKey) || 0) + 1)
        }
      })

      const closedChartData = Array.from(dailyClosed.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, value]) => ({
          label: new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value,
        }))

      return {
        createdCount,
        previousCreatedCount,
        createdPercentageChange,
        createdChartData,
        createdTrend: (createdPercentageChange > 0
          ? "up"
          : createdPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
        closedCount,
        previousClosedCount,
        closedPercentageChange,
        closedChartData,
        closedTrend: (closedPercentageChange > 0
          ? "up"
          : closedPercentageChange < 0
            ? "down"
            : "neutral") as "up" | "down" | "neutral",
      }
    }, [dataStore.allIssues, dateRange, comparisonDateRange])

    // Calculate releases metrics
    const releasesMetrics = useMemo(() => {
      const now = new Date()

      // Helper to get Monday of the current week
      const getMondayOfWeek = (date: Date): Date => {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
        return new Date(d.setDate(diff))
      }

      // Helper to get Sunday of a given Monday
      const getSundayOfWeek = (monday: Date): Date => {
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        return sunday
      }

      // Get Monday of current week
      const currentWeekMonday = getMondayOfWeek(now)
      currentWeekMonday.setHours(0, 0, 0, 0)

      // Calculate start of 12 weeks ago (Monday of that week)
      const twelveWeeksAgoMonday = new Date(currentWeekMonday)
      twelveWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 12 * 7)

      // Calculate start of 6 weeks ago (Monday of that week)
      const sixWeeksAgoMonday = new Date(currentWeekMonday)
      sixWeeksAgoMonday.setDate(currentWeekMonday.getDate() - 6 * 7)

      // Filter releases for last 12 weeks (excluding drafts and prereleases)
      const recentReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= twelveWeeksAgoMonday && !release.draft && !release.prerelease,
      )

      // Count total releases in last 12 weeks
      const totalReleases = recentReleases.length

      // Create weekly buckets for trend (last 12 weeks)
      const weeklyTrend: Array<{ label: string; value: number }> = []
      for (let i = 0; i < 12; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (11 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const count = recentReleases.filter(
          (release) => release.createdAtDate >= weekMonday && release.createdAtDate <= weekSunday,
        ).length

        weeklyTrend.push({
          label: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          value: count,
        })
      }

      // Calculate percentage change (comparing first 6 weeks vs last 6 weeks)
      const firstHalf = weeklyTrend.slice(0, 6).reduce((sum, week) => sum + week.value, 0)
      const secondHalf = weeklyTrend.slice(6, 12).reduce((sum, week) => sum + week.value, 0)
      const percentageChange = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0

      // Create stacked bar chart data for last 6 weeks by repository
      const last6WeeksReleases = dataStore.allReleases.filter(
        (release) =>
          release.createdAtDate >= sixWeeksAgoMonday && !release.draft && !release.prerelease,
      )

      // Get all unique repositories
      const repositories = Array.from(new Set(last6WeeksReleases.map((r) => r.repoName))).sort()

      // Create weekly buckets for stacked chart
      const stackedData: Array<Record<string, string | number>> = []
      for (let i = 0; i < 6; i++) {
        const weekMonday = new Date(currentWeekMonday)
        weekMonday.setDate(currentWeekMonday.getDate() - (5 - i) * 7)
        const weekSunday = getSundayOfWeek(weekMonday)
        weekSunday.setHours(23, 59, 59, 999)

        const weekData: Record<string, string | number> = {
          week: weekMonday.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        }

        // Count releases per repository for this week
        repositories.forEach((repo) => {
          const count = last6WeeksReleases.filter(
            (release) =>
              release.repoName === repo &&
              release.createdAtDate >= weekMonday &&
              release.createdAtDate <= weekSunday,
          ).length
          weekData[repo] = count
        })

        stackedData.push(weekData)
      }

      return {
        totalReleases,
        weeklyTrend,
        percentageChange,
        trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
          | "up"
          | "down"
          | "neutral",
        stackedData,
        repositories,
      }
    }, [dataStore.allReleases])

    // Calculate activity data for Activity Heatmap
    const activityData = useMemo(() => {
      const activityMap = new Map<string, number>()

      // Count commits per day
      dataStore.allCommits.forEach((commit) => {
        const dateKey = commit.timestampDate.toISOString().split("T")[0]
        activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
      })

      // Count PRs per day (use createdAtDate)
      dataStore.allPullRequests.forEach((pr) => {
        if (pr.createdAtDate) {
          const dateKey = pr.createdAtDate.toISOString().split("T")[0]
          activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
        }
      })

      // Count issues per day (use createdAtDate)
      dataStore.allIssues.forEach((issue) => {
        if (issue.createdAtDate) {
          const dateKey = issue.createdAtDate.toISOString().split("T")[0]
          activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
        }
      })

      // Convert to array format expected by ActivityHeatmap
      return Array.from(activityMap.entries()).map(([dateStr, count]) => ({
        date: new Date(dateStr),
        count,
      }))
    }, [dataStore.allCommits, dataStore.allPullRequests, dataStore.allIssues])

    // Helper function to format dates as relative time
    const formatRelativeDate = useCallback((date: Date): string => {
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffSecs < 60) return "just now"
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
      if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
      return `${Math.floor(diffDays / 365)}y ago`
    }, [])

    // Calculate recent activities for DataTable (all activities in selected time range)
    const recentActivitiesTableData = useMemo(() => {
      interface ActivityItem {
        id: string
        date: Date
        type: "commit" | "pr" | "issue" | "release"
        title: string
        repo: string
        contributor: string
        url: string
      }

      const activities: ActivityItem[] = []

      // Add commits
      dataStore.allCommits.forEach((commit) => {
        if (commit.timestampDate >= dateRange) {
          activities.push({
            id: `commit-${commit.hash}`,
            date: commit.timestampDate,
            type: "commit",
            title: commit.message.slice(0, 80) + (commit.message.length > 80 ? "..." : ""),
            repo: commit.repoName,
            contributor: commit.authorHandle,
            url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          })
        }
      })

      // Add PRs
      dataStore.allPullRequests.forEach((pr) => {
        if (pr.createdAtDate && pr.createdAtDate >= dateRange) {
          activities.push({
            id: `pr-${pr.id}`,
            date: pr.createdAtDate,
            type: "pr",
            title: pr.title,
            repo: pr.repoName,
            contributor: pr.creatorHandle,
            url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
          })
        }
      })

      // Add issues
      dataStore.allIssues.forEach((issue) => {
        if (issue.createdAtDate && issue.createdAtDate >= dateRange) {
          activities.push({
            id: `issue-${issue.id}`,
            date: issue.createdAtDate,
            type: "issue",
            title: issue.title,
            repo: issue.repoName,
            contributor: issue.creatorHandle,
            url: `https://github.com/${issue.repoName}/issues/${issue.number}`,
          })
        }
      })

      // Add releases
      dataStore.allReleases.forEach((release) => {
        if (release.createdAtDate && release.createdAtDate >= dateRange) {
          activities.push({
            id: `release-${release.id}`,
            date: release.createdAtDate,
            type: "release",
            title: `Released ${release.tagName}${release.name ? ` - ${release.name}` : ""}`,
            repo: release.repoName,
            contributor: release.authorHandle,
            url: `https://github.com/${release.repoName}/releases/tag/${release.tagName}`,
          })
        }
      })

      // Sort by date descending (most recent first)
      return activities
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map((activity) => ({
          id: activity.id,
          type: activity.type,
          title: activity.title,
          repo: activity.repo,
          contributor: activity.contributor,
          date: formatRelativeDate(activity.date),
          url: activity.url,
        }))
    }, [
      dataStore.allCommits,
      dataStore.allPullRequests,
      dataStore.allIssues,
      dataStore.allReleases,
      dateRange,
      formatRelativeDate,
    ])

    // Calculate top teams and contributors based on selected metric
    const topPerformers = useMemo(() => {
      const currentPeriodStart = dateRange
      const selectedInsight = dashboardStore.selectedInsight

      if (!selectedInsight) {
        return { topTeams: [], topContributors: [] }
      }

      // Determine metric type from selected insight
      const metricType = selectedInsight.id // pr-throughput, features, bugfixes, maintenance

      // Helper to get metric value for a commit/PR based on type
      const getCommitValue = () => 1 // Count
      const getPRValue = () => 1 // Count

      // Aggregate by team
      const teamMetrics = new Map<string, number>()
      const contributorMetrics = new Map<string, number>()

      if (metricType === "pr-throughput") {
        // Filter PRs by time range
        const filteredPRs = dataStore.allPullRequests.filter(
          (pr) => pr.createdAtDate >= currentPeriodStart && pr.state === "merged",
        )

        // Count by team
        filteredPRs.forEach((pr) => {
          const team = metadataStore.getTeam(pr.creatorHandle)
          if (team) {
            teamMetrics.set(team, (teamMetrics.get(team) || 0) + getPRValue())
          }

          // Count by contributor
          contributorMetrics.set(
            pr.creatorHandle,
            (contributorMetrics.get(pr.creatorHandle) || 0) + getPRValue(),
          )
        })
      } else {
        // For commit-based metrics (features, bugfixes, maintenance)
        let filteredCommits = dataStore.allCommits.filter(
          (c) => c.timestampDate >= currentPeriodStart,
        )

        // Filter by commit type
        if (metricType === "features") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "feature")
        } else if (metricType === "bugfixes") {
          filteredCommits = filteredCommits.filter((c) => c.commitType === "bugfix")
        } else if (metricType === "maintenance") {
          filteredCommits = filteredCommits.filter(
            (c) => c.commitType && c.commitType !== "feature" && c.commitType !== "bugfix",
          )
        }

        // Count by team and contributor
        filteredCommits.forEach((commit) => {
          const team = metadataStore.getTeam(commit.authorHandle)
          if (team) {
            teamMetrics.set(team, (teamMetrics.get(team) || 0) + getCommitValue())
          }

          // Count by contributor
          contributorMetrics.set(
            commit.authorHandle,
            (contributorMetrics.get(commit.authorHandle) || 0) + getCommitValue(),
          )
        })
      }

      // Sort and rank all teams and contributors (pagination handled by DataTable)
      const topTeams = Array.from(teamMetrics.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([team, value], index) => ({
          rank: index + 1,
          team,
          value,
        }))

      const topContributors = Array.from(contributorMetrics.entries())
        .sort(([, a], [, b]) => b - a)
        .map(([contributor, value], index) => ({
          rank: index + 1,
          contributor,
          value,
        }))

      return { topTeams, topContributors }
    }, [
      dashboardStore.selectedInsight,
      dataStore.allCommits,
      dataStore.allPullRequests,
      dateRange,
      metadataStore,
    ])

    // Create tabs
    const tabs: TabDefinition[] = [
      {
        id: "insights",
        label: i18nStore.t("tabPerformance"),
        content: (
          <div className="space-y-6">
            {/* Always show quick insight cards */}
            <InsightDashboard
              store={dashboardStore}
              tableTitle={i18nStore.t("chartDetails")}
              dataTableTranslations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              hideTable={true}
            />

            {/* Show performance explanation and details table when performance is selected */}
            {dashboardStore.selectedInsight?.id === "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <div className="space-y-4">
                  {/* Performance Explanation */}
                  <div className="rounded-lg border bg-card p-6">
                    <h3 className="text-lg font-semibold mb-3">
                      {i18nStore.t("performanceMethodologyTitle")}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {i18nStore.t("performanceMethodologyDescription")}
                    </p>
                    <ul className="space-y-2 text-sm mb-4">
                      <li>
                        <strong>{i18nStore.t("performanceQualityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceQualityDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceThroughputWeight")}</strong>:{" "}
                        {i18nStore.t("performanceThroughputDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceImpactWeight")}</strong>:{" "}
                        {i18nStore.t("performanceImpactDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceProblemSolvingWeight")}</strong>:{" "}
                        {i18nStore.t("performanceProblemSolvingDesc")}
                      </li>
                      <li>
                        <strong>{i18nStore.t("performanceVersatilityWeight")}</strong>:{" "}
                        {i18nStore.t("performanceVersatilityDesc")}
                      </li>
                    </ul>
                    <p className="text-sm text-muted-foreground italic border-t pt-3">
                      {i18nStore.t("performanceScoreNote")}
                    </p>
                  </div>

                  {/* Performance Daily Values Table */}
                  <DataTable
                    title={i18nStore.t("performanceDailyValuesTitle")}
                    columns={dashboardStore.selectedTableColumns}
                    data={dashboardStore.selectedTableData}
                    enableSearch={true}
                    searchPlaceholder={i18nStore.t("dataTableSearch")}
                    enableCsvExport={true}
                    csvFilename="performance-daily-values.csv"
                    initialSorting={[{ id: "date", desc: true }]}
                    translations={{
                      filtersButton: i18nStore.t("dataTableFilters"),
                      filterBy: i18nStore.t("dataTableFilterBy"),
                      noResults: i18nStore.t("dataTableNoResults"),
                      rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                      page: i18nStore.t("dataTablePage"),
                      of: i18nStore.t("dataTableOf"),
                      totalRows: i18nStore.t("dataTableTotalRows"),
                    }}
                  />
                </div>
              )}

            {/* Two-column grid with top teams and top contributors (only when NOT performance) */}
            {dashboardStore.selectedInsight?.id !== "performance" && (
              <div className="grid gap-6 md:grid-cols-2">
                {/* Left column: Top Teams */}
                <DataTable
                  title={i18nStore.t("topTeamsTitle")}
                  columns={[
                    {
                      accessorKey: "rank",
                      header: "#",
                    },
                    {
                      accessorKey: "team",
                      header: i18nStore.t("tableColumnTeam"),
                      cell: ({
                        row,
                      }: {
                        row: {
                          getValue: (key: string) => string
                        }
                      }) => {
                        const teamName = row.getValue("team") as string
                        return (
                          <div className="flex items-center gap-3">
                            <GenerativeAvatar name={teamName} type="team" size="md" />
                            <button
                              type="button"
                              onClick={() => navigateToTeam(teamName)}
                              className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                            >
                              {teamName}
                            </button>
                          </div>
                        )
                      },
                    },
                    {
                      accessorKey: "value",
                      header: i18nStore.t("tableColumnValue"),
                    },
                  ]}
                  data={topPerformers.topTeams}
                  enableSearch={false}
                  enableCsvExport={false}
                  translations={{
                    filtersButton: i18nStore.t("dataTableFilters"),
                    filterBy: i18nStore.t("dataTableFilterBy"),
                    noResults: i18nStore.t("dataTableNoResults"),
                    rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                    page: i18nStore.t("dataTablePage"),
                    of: i18nStore.t("dataTableOf"),
                    totalRows: i18nStore.t("dataTableTotalRows"),
                  }}
                />

                {/* Right column: Top Contributors */}
                <DataTable
                  title={i18nStore.t("topContributorsTitle")}
                  columns={[
                    {
                      accessorKey: "rank",
                      header: "#",
                    },
                    {
                      accessorKey: "contributor",
                      header: i18nStore.t("tableColumnContributor"),
                      cell: ({
                        row,
                      }: {
                        row: {
                          getValue: (key: string) => string
                        }
                      }) => {
                        const handle = row.getValue("contributor") as string
                        const avatarUrl = getGitHubAvatarUrl(handle)
                        const fallback = handle.slice(0, 2).toUpperCase()
                        return (
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarImage src={avatarUrl} alt={handle} />
                              <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                            </Avatar>
                            <button
                              type="button"
                              onClick={() => navigateToContributor(handle)}
                              className="text-primary hover:underline cursor-pointer bg-transparent border-none p-0"
                            >
                              {handle}
                            </button>
                          </div>
                        )
                      },
                    },
                    {
                      accessorKey: "value",
                      header: i18nStore.t("tableColumnValue"),
                    },
                  ]}
                  data={topPerformers.topContributors}
                  enableSearch={false}
                  enableCsvExport={false}
                  translations={{
                    filtersButton: i18nStore.t("dataTableFilters"),
                    filterBy: i18nStore.t("dataTableFilterBy"),
                    noResults: i18nStore.t("dataTableNoResults"),
                    rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                    page: i18nStore.t("dataTablePage"),
                    of: i18nStore.t("dataTableOf"),
                    totalRows: i18nStore.t("dataTableTotalRows"),
                  }}
                />
              </div>
            )}

            {/* Details Table - Moved to the end (only when NOT performance) */}
            {dashboardStore.selectedInsight?.id !== "performance" &&
              dashboardStore.selectedTableData.length > 0 &&
              dashboardStore.selectedTableColumns.length > 0 && (
                <DataTable
                  title={i18nStore.t("chartDetails")}
                  columns={dashboardStore.selectedTableColumns}
                  data={dashboardStore.selectedTableData}
                  enableSearch={true}
                  searchPlaceholder={i18nStore.t("dataTableSearch")}
                  enableCsvExport={true}
                  csvFilename="insight-data.csv"
                  initialSorting={[{ id: "date", desc: true }]}
                  translations={{
                    filtersButton: i18nStore.t("dataTableFilters"),
                    filterBy: i18nStore.t("dataTableFilterBy"),
                    noResults: i18nStore.t("dataTableNoResults"),
                    rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                    page: i18nStore.t("dataTablePage"),
                    of: i18nStore.t("dataTableOf"),
                    totalRows: i18nStore.t("dataTableTotalRows"),
                  }}
                />
              )}
          </div>
        ),
      },
      {
        id: "commits",
        label: i18nStore.t("tabScore"),
        content: (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <StackedBarChart
                title={i18nStore.t("chartCommitsByClassification")}
                description={i18nStore.t("chartCommitsByClassificationDesc")}
                data={barChartData.classificationData}
                categoryKey="classification"
                stackKeys={barChartData.complexityOrder}
              />
              <StackedBarChart
                title={i18nStore.t("chartCommitsBySize")}
                description={i18nStore.t("chartCommitsBySizeDesc")}
                data={barChartData.sizeData}
                categoryKey="size"
                stackKeys={barChartData.activeTypes}
              />
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <StackedBarChart
                title={i18nStore.t("chartCommitScoresByWeek")}
                description={i18nStore.t("chartCommitScoresByWeekDesc")}
                data={weeklyScoresData.scoresBySize}
                categoryKey="week"
                stackKeys={weeklyScoresData.complexityOrder || []}
              />
              <StackedBarChart
                title={i18nStore.t("chartCommitScoresByClassificationWeek")}
                description={i18nStore.t("chartCommitScoresByClassificationWeekDesc")}
                data={weeklyScoresData.scoresByClassification}
                categoryKey="week"
                stackKeys={weeklyScoresData.classifications || []}
              />
            </div>
          </div>
        ),
      },
      {
        id: "prs",
        label: i18nStore.t("tabPRs"),
        content: (
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <MetricsCard
                title={i18nStore.t("chartPRsMerged")}
                description={i18nStore.tf("chartPRsMergedDesc", {
                  count: prMetrics.currentCount.toString(),
                  prevCount: prMetrics.previousCount.toString(),
                })}
                percentage={Math.abs(prMetrics.percentageChange)}
                trend={prMetrics.trend}
                chartData={prMetrics.prChartData}
                chartType="bar"
              />
              <MetricsCard
                title={i18nStore.t("chartCodeChanges")}
                description={i18nStore.t("chartCodeChangesDesc")}
                percentage={Math.abs(prMetrics.codeChangesTrend)}
                trend={prMetrics.codeChangesTrendDirection}
                chartData={prMetrics.codeChangesChartData}
                chartType="line"
              />
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <StackedBarChart
                title={i18nStore.t("chartPRsMergedByWeek")}
                description={i18nStore.t("chartPRsMergedByWeekDesc")}
                data={weeklyPRData.prsByWeekByTeam}
                categoryKey="week"
                stackKeys={weeklyPRData.teams}
              />
              <DivergingBarChart
                title={i18nStore.t("chartCodeChangesByWeek")}
                description={i18nStore.t("chartCodeChangesByWeekDesc")}
                data={weeklyPRData.codeChangesByWeek}
                positiveLabel={i18nStore.t("linesAdded")}
                negativeLabel={i18nStore.t("linesRemoved")}
              />
            </div>
          </div>
        ),
      },
      {
        id: "issues",
        label: i18nStore.t("tabIssues"),
        content: (
          <div className="grid gap-6 md:grid-cols-2">
            <MetricsCard
              title={i18nStore.t("chartIssuesCreated")}
              description={i18nStore.tf("chartIssuesCreatedDesc", {
                count: issueMetrics.createdCount.toString(),
                prevCount: issueMetrics.previousCreatedCount.toString(),
              })}
              percentage={Math.abs(issueMetrics.createdPercentageChange)}
              trend={issueMetrics.createdTrend}
              chartData={issueMetrics.createdChartData}
              chartType="bar"
            />
            <MetricsCard
              title={i18nStore.t("chartIssuesClosed")}
              description={i18nStore.tf("chartIssuesClosedDesc", {
                count: issueMetrics.closedCount.toString(),
                prevCount: issueMetrics.previousClosedCount.toString(),
              })}
              percentage={Math.abs(issueMetrics.closedPercentageChange)}
              trend={issueMetrics.closedTrend}
              chartData={issueMetrics.closedChartData}
              chartType="bar"
            />
          </div>
        ),
      },
      {
        id: "releases",
        label: i18nStore.t("tabReleases"),
        content: (
          <div className="grid gap-6">
            {/* Releases Trend Card */}
            <MetricsCard
              title={i18nStore.t("releasesTitle")}
              description={i18nStore.t("releasesDescription")}
              percentage={Math.abs(releasesMetrics.percentageChange)}
              trend={releasesMetrics.trend}
              chartData={releasesMetrics.weeklyTrend}
              chartType="line"
              trendLabel={i18nStore.t("chartTrend")}
            />

            {/* Stacked Bar Chart - Releases by Repository */}
            <StackedBarChart
              title={i18nStore.t("releasesChartTitle")}
              description={i18nStore.t("releasesChartDescription")}
              data={releasesMetrics.stackedData}
              categoryKey="week"
              stackKeys={releasesMetrics.repositories}
            />
          </div>
        ),
      },
      {
        id: "activity",
        label: i18nStore.t("tabActivity"),
        content: (
          <div className="space-y-6">
            <ActivityHeatmap
              title={i18nStore.t("chartActivityTitle")}
              description={i18nStore.t("chartActivityDescription")}
              data={activityData}
            />
            <DataTable
              title={i18nStore.t("recentActivity")}
              columns={[
                {
                  accessorKey: "contributor",
                  header: i18nStore.t("tableColumnContributor"),
                  cell: ({
                    row,
                  }: {
                    row: {
                      original: { contributor: string; type: string }
                      getValue: (key: string) => string
                    }
                  }) => {
                    const handle = row.getValue("contributor") as string
                    const avatarUrl = getGitHubAvatarUrl(handle)
                    const fallback = handle.slice(0, 2).toUpperCase()
                    const type = row.original.type
                    const icon =
                      type === "commit" ? (
                        <GitCommit className="h-4 w-4 text-blue-500" />
                      ) : type === "pr" ? (
                        <GitMerge className="h-4 w-4 text-purple-500" />
                      ) : type === "issue" ? (
                        <GitPullRequest className="h-4 w-4 text-orange-500" />
                      ) : (
                        <Tag className="h-4 w-4 text-amber-500" />
                      )
                    return (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => navigateToContributor(handle)}
                          className="cursor-pointer bg-transparent border-none p-0 transition-transform hover:scale-110 shrink-0"
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={avatarUrl} alt={handle} />
                            <AvatarFallback className="text-xs">{fallback}</AvatarFallback>
                          </Avatar>
                        </button>
                        <div className="flex items-center gap-2">
                          {icon}
                          <span>{handle}</span>
                        </div>
                      </div>
                    )
                  },
                },
                {
                  accessorKey: "title",
                  header: i18nStore.t("tableColumnTitle"),
                  cell: ({
                    row,
                  }: {
                    row: {
                      original: { url: string; repo: string }
                      getValue: (key: string) => string
                    }
                  }) => (
                    <div className="flex flex-col gap-0.5">
                      <a
                        href={row.original.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {row.getValue("title")}
                      </a>
                      <span className="text-xs text-muted-foreground">{row.original.repo}</span>
                    </div>
                  ),
                },
                {
                  accessorKey: "date",
                  header: i18nStore.t("tableColumnDate"),
                },
              ]}
              data={recentActivitiesTableData}
              enableSearch={true}
              searchPlaceholder={i18nStore.t("dataTableSearch")}
              enableCsvExport={true}
              csvFilename="recent-activity.csv"
              translations={{
                filtersButton: i18nStore.t("dataTableFilters"),
                filterBy: i18nStore.t("dataTableFilterBy"),
                noResults: i18nStore.t("dataTableNoResults"),
                rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
                page: i18nStore.t("dataTablePage"),
                of: i18nStore.t("dataTableOf"),
                totalRows: i18nStore.t("dataTableTotalRows"),
              }}
            />
          </div>
        ),
      },
    ]

    // Log dashboard render
    logger.debug("Rendering OrganizationDashboard", {
      component: "OrganizationDashboard",
      commits: dataStore.allCommits.length,
      prs: dataStore.allPullRequests.length,
    })

    return (
      <TabsWithStore
        tabs={tabs}
        selectedTab={selectedTab}
        defaultSelectedTab="insights"
        onTabChange={onTabChange}
      />
    )
  },
)
